import React, {useEffect, useState, useRef} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Button, FileInput, Intent, Position, ProgressBar, Toaster} from '@blueprintjs/core';
import {IconNames} from '@blueprintjs/icons';

import {ApiStatus} from '../../../models';
import {IState} from '../../../store/reducers';
import {uploadCsv} from '../../../store/actions/codeBlockActions';
import {useCurrentWorkspace} from '../../../hooks';
import {DEFAULT_MODE} from './constants';

interface UploadCSVProps {
  uid: string;
  mode: string;
  onUpload: (value: any) => void;
}

const MAX_SIZE_MB = 10 * 1024 * 1024;

export const csvToaster = Toaster.create({
  position: Position.TOP
});

const UploadCSV = ({
  uid,
  mode,
  onUpload,
}: UploadCSVProps) => {
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const inputRef = useRef<any>(null);

  const blockState = useSelector((state: IState) =>
    state.codeBlock.results.find(r => r.blockUid === uid));
  const currentWorkspace = useCurrentWorkspace();
  const dispatch = useDispatch();

  const codeBlockResult = blockState?.result;
  const codeBlockError = blockState?.error;
  const uploadProgress = blockState?.progress || 0;
  const uploading: boolean = blockState?.uploadingCsvStatus === ApiStatus.LOADING;
  const showResult: boolean = !!csvFile && !uploading && Array.isArray(codeBlockResult);

  useEffect(() => {
    if (codeBlockResult && Array.isArray(codeBlockResult)) {
      const formatted = JSON.stringify(codeBlockResult, null, 2);
      onUpload(formatted);
    }
  }, [codeBlockResult]);

  useEffect(() => {
    if (codeBlockError) {
      csvToaster.show({
        message: codeBlockError,
        intent: Intent.DANGER,
      });
    }
  }, [codeBlockError]);

  const onClick = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const _onCSVFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      if (!file) return;

      const fileName = file.name.toLowerCase()

      if (!fileName.endsWith('.csv')) {
        csvToaster.show({
          message: `Only .csv files are allowed.`,
          intent: Intent.DANGER,
        });

        event.target.value = '';
        return;
      }

      if (file.size > MAX_SIZE_MB) {
        csvToaster.show({
          message: `File size exceeds the 10MB limit. Please choose a smaller file.`,
          intent: Intent.DANGER,
        });
        event.target.value = '';
        return;
      }

      setCsvFile(file);

      const startTime = new Date().getTime().toString();
      const workspaceId = currentWorkspace?.id.toString();

      if (workspaceId && uid) {
        dispatch(uploadCsv(uid, workspaceId, startTime, file));
      }
    }
  };

  if (mode !== DEFAULT_MODE) {
    return null;
  }

  return (
    <div className="csv-file-input">
      <Button
        minimal={true}
        outlined={true}
        icon={IconNames.FOLDER_OPEN}
        text="Import CSV to convert into JSON"
        onClick={onClick}
      />
      <FileInput
        text={"Choose CSV file... (max 10MB)"}
        onInputChange={_onCSVFileChange}
        inputProps={{
          accept: 'text/csv',
          ref: inputRef,
        }}
      />
      {
        uploading && <ProgressBar className="progress-bar" intent="primary" value={uploadProgress / 100}/>
      }
      {
        showResult && (
          <div className="result">
            Uploaded <strong>{codeBlockResult?.length || 0}</strong> rows from CSV
            file <strong>{csvFile?.name}</strong>
          </div>
        )
      }
    </div>
  );
}

export default UploadCSV;
